import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowUpRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { IMAGES, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { GuideRequestButton } from "@/components/GuideRequest";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const AUDIENCE_TABS = [
  { id: "general", label: "General" },
  { id: "investor", label: "Investor" },
  { id: "tourist", label: "Tourist" },
  { id: "returnee", label: "Returnee & Relocator" },
  { id: "expat", label: "Expat" },
];

const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 900);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }))}
      data-testid="back-to-top"
      aria-label="Back to top"
      className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center border border-jawa-espresso/15 bg-jawa-espresso text-jawa-ivory shadow-lg transition-colors hover:bg-jawa-coral"
    >
      <ArrowUp className="h-4 w-4" />
    </button>
  );
};

const FreeOnlineCard = ({ image, onExplore }) => (
  <article
    data-testid="collection-card-free-online"
    className="group grid overflow-hidden border border-jawa-turquoise/50 bg-white md:grid-cols-2"
  >
    <div className="relative h-64 overflow-hidden md:h-auto">
      <img
        src={image}
        alt="Lagos waterfront — the Free JÀWÀ A–Z Guide"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
        Free — read online
      </span>
    </div>
    <div className="flex flex-col justify-center p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-jawa-turquoise">For everyone</p>
      <h3 className="mt-3 font-serif text-3xl font-medium tracking-tight text-jawa-espresso">
        The Free A–Z Guide
      </h3>
      <p className="mt-4 max-w-lg text-sm leading-relaxed text-jawa-espresso/70">
        Our full A–Z of contemporary Lagos life — accommodation, banking, food, healthcare, transport,
        schools, wellness and everything between. Read it right here: no payment, no sign-up, no email gate.
      </p>
      <div className="mt-8">
        <button
          onClick={onExplore}
          data-testid="explore-free-guide"
          className="inline-flex items-center gap-2 bg-jawa-espresso px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
        >
          <BookOpen className="h-4 w-4" /> Explore Free Guide
        </button>
      </div>
    </div>
  </article>
);

const CollectionCard = ({ product }) => {
  const slug = product.audience;
  return (
    <article
      id={`collection-${slug}`}
      data-testid={`collection-card-${slug}`}
      className="group flex h-full scroll-mt-28 flex-col overflow-hidden border border-jawa-gold/40 bg-jawa-espresso"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={product.image_url}
          alt={product.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 border border-jawa-gold/60 bg-jawa-nearblack/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-jawa-gold">
          {slug === "returnee" ? "Returnee & Relocator" : `${slug} collection`}
        </span>
        <span className="absolute right-4 top-4 bg-jawa-turquoise px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-jawa-nearblack">
          Members-only
        </span>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <h3 className="font-serif text-2xl font-medium tracking-tight text-jawa-ivory">{product.title}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-jawa-ivory/65">{product.description}</p>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-jawa-ivory/10 pt-5">
          <span className="text-xs uppercase tracking-[0.2em] text-jawa-ivory/50">Included with membership</span>
          <Link
            to={`/membership/${slug}`}
            data-testid={`collection-membership-${slug}`}
            className="flex items-center gap-1.5 bg-jawa-coral px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-coral/90"
          >
            View Membership <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const AirportGuideBlock = ({ product }) => (
  <div
    className="grid overflow-hidden border border-jawa-coral/40 bg-white md:grid-cols-2"
    data-testid="airport-guide-block"
  >
    <div className="relative h-64 md:h-auto">
      <img
        src="/lagos-airport-terminal.jpg"
        alt="The Free Nigerian Airport Guide"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
        Free PDF
      </span>
    </div>
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-jawa-coral">Free download — delivered by email</p>
      <h3 className="mt-3 font-serif text-3xl font-medium tracking-tight text-jawa-espresso">
        The Free Nigerian Airport Guide
      </h3>
      <p className="mt-4 text-sm leading-relaxed text-jawa-espresso/70">
        {product?.description ||
          "Your practical arrival checklist for Nigerian airports — immigration, SIM cards, pickups and the first hour on the ground."}
      </p>
      <GuideRequestButton
        kind="airport"
        label="Get the Free Airport Guide"
        testid="airport-guide-download"
        className="mt-8 inline-flex items-center gap-2 bg-jawa-espresso px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
      />
    </div>
  </div>
);

const SchoolGuideCard = ({ product }) => (
  <div className="flex h-full flex-col overflow-hidden border border-jawa-coral/40 bg-white" data-testid="school-guide-block">
    <div className="relative h-44">
      <img
        src={product?.image_url || "/school-guide.jpg"}
        alt="Nigerian schoolchildren in smart school uniforms"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
        Free PDF
      </span>
    </div>
    <div className="flex flex-1 flex-col p-8">
      <h3 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso">
        Best Nigerian Private School Guide
      </h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-jawa-espresso/70">
      {product?.description ||
        "Researching schools in Nigeria — the right questions, admissions timelines and how to shortlist with confidence."}
    </p>
    <GuideRequestButton
      kind="school"
      label="Get the Free School Guide"
      testid="school-guide-download"
      className="mt-6 inline-flex w-fit items-center gap-2 bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
    />
    </div>
  </div>
);

export default function GuidesPage() {
  const [products, setProducts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [audience, setAudience] = useState("general");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/topics?audience=${audience}`).then((res) => setTopics(res.data)).catch(() => setTopics([]));
  }, [audience]);

  const freeGuide = products.find((p) => p.category === "free");
  const airportGuide = products.find((p) => p.category === "free_airport");
  const schoolGuide = products.find((p) => p.category === "free_school");
  const collections = products.filter((p) => p.category === "collection");
  const flagship = products.filter((p) => p.category === "guide" && !p.title.startsWith("Detty December"));
  const dettyGuides = products.filter((p) => p.category === "guide" && p.title.startsWith("Detty December"));

  const byLetter = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? topics.filter((t) => `${t.title} ${t.summary}`.toLowerCase().includes(q))
      : topics;
    const map = {};
    for (const t of filtered) {
      const L = (t.letter || "Z").toUpperCase();
      (map[L] = map[L] || []).push(t);
    }
    return map;
  }, [topics, query]);

  const scrollTo = (L) => {
    const el = document.getElementById(`letter-${L}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-jawa-ivory" data-testid="guides-page">
      <Nav />
      <Seo
        title="A–Z Guides — Free Resources & Member Collections | JÀWÀ Africa"
        description="Browse the JÀWÀ A–Z: a free general guide, free Nigerian airport and school PDFs, and members-only A–Z collections for investors, tourists, returnees and expats."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="The JÀWÀ A–Z Guides" title="Know before you go." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Practical, beautifully brief guides for every kind of journey — one free general guide,
            and four bespoke collections written for your audience.
          </p>
        </FadeUp>

        <FadeUp className="mt-10">
          <p className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral" data-testid="free-resources-label">
            Free downloadable resources — no payment, no membership
          </p>
          <AirportGuideBlock product={airportGuide} />
        </FadeUp>
        <FadeUp className="mt-6 grid gap-6 md:grid-cols-2">
          <SchoolGuideCard product={schoolGuide} />
          <div className="flex h-full flex-col justify-center border border-jawa-turquoise/40 bg-jawa-espresso p-8" data-testid="free-resources-why">
            <h3 className="font-serif text-2xl font-medium text-jawa-ivory">Why free?</h3>
            <p className="mt-3 text-sm leading-relaxed text-jawa-ivory/70">
              Because the first decisions are the most important. Both PDFs are free — we simply ask for
              your email address (with your explicit consent) so we can send you the download link, and the
              download starts immediately too. The A–Z below is free to read online, no email needed.
            </p>
            <Link
              to="/emergency"
              data-testid="guides-emergency-link"
              className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-ivory"
            >
              Emergency contact numbers — free, on-page <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeUp>

        {flagship.length > 0 && (
          <FadeUp className="mt-24">
            <SectionHeading overline="The flagship guides — instant PDF" title="The full A–Z. Written for your journey." />
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-jawa-espresso/60">
              The complete researched editions — every state, every budget level, every practical step.
              Facts separated from estimates. Secure online checkout, PDF delivered immediately.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="flagship-guides-grid">
              {flagship.map((p, i) => (
                <FadeUp key={p._id} delay={i * 0.05}>
                  <Link
                    to={`/shop/p/${p._id}`}
                    data-testid={`flagship-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex h-full flex-col overflow-hidden border border-jawa-espresso/10 bg-white transition-colors hover:border-jawa-coral/60"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Instant PDF</span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-serif text-xl font-medium text-jawa-espresso">{p.title}</h3>
                      <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-4">
                        <span className="font-serif text-2xl font-medium text-jawa-espresso">
                          <Price amount={p.price} from={p.currency || "USD"} />
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-espresso">
                          Buy <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
            {dettyGuides.length > 0 && (
              <div className="mt-6 grid gap-6 sm:grid-cols-2" data-testid="detty-guides-grid">
                {dettyGuides.map((p, i) => (
                  <FadeUp key={p._id} delay={i * 0.05}>
                    <Link
                      to={`/shop/p/${p._id}`}
                      data-testid={`detty-guide-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group flex flex-col overflow-hidden border border-jawa-espresso/10 bg-white transition-colors hover:border-jawa-coral/60 sm:flex-row"
                    >
                      <div className="relative h-40 overflow-hidden sm:h-auto sm:w-2/5">
                        <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <span className="absolute left-4 top-4 bg-jawa-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-jawa-nearblack">Season guide</span>
                      </div>
                      <div className="flex flex-1 flex-col justify-between p-6">
                        <div>
                          <h3 className="font-serif text-xl font-medium text-jawa-espresso">{p.title}</h3>
                          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-jawa-espresso/60">{p.description}</p>
                        </div>
                        <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-4">
                          <span className="font-serif text-2xl font-medium text-jawa-espresso">
                            <Price amount={p.price} from={p.currency || "USD"} />
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-espresso">
                            Buy <ArrowUpRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </FadeUp>
                ))}
              </div>
            )}
          </FadeUp>
        )}

        <FadeUp className="mt-24">
          <SectionHeading overline="The collection" title="One free A–Z. Four bespoke collections." />
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-jawa-espresso/60">
            Start with the free online guide — then go deeper with the A–Z written for your journey.
            Each collection lives online, members-only, always current. Memberships start at £4.99/month.
          </p>
        </FadeUp>
        <FadeUp className="mt-10">
          <FreeOnlineCard
            image={(freeGuide && freeGuide.image_url) || IMAGES.lagosSunny}
            onExplore={() => {
              setAudience("general");
              document.getElementById("browse")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </FadeUp>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="collections-grid">
          {collections.map((p, i) => (
            <FadeUp key={p._id} delay={i * 0.06}>
              <CollectionCard product={p} />
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-28" >
          <div id="browse" className="scroll-mt-24">
            <SectionHeading overline="Browse the A–Z" title="Every letter. In order." />
          </div>
        </FadeUp>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="audience-tabs">
          {AUDIENCE_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setAudience(t.id)}
              data-testid={`audience-tab-${t.id}`}
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                audience === t.id
                  ? "bg-jawa-espresso text-jawa-ivory"
                  : "border border-jawa-espresso/20 text-jawa-espresso/60 hover:text-jawa-espresso"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="sticky top-[72px] z-30 mt-10 border-y border-jawa-stone bg-jawa-ivory/95 py-4 backdrop-blur-md" data-testid="letter-index">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the A–Z — try 'schools', 'SIM', 'property'…"
            data-testid="guides-search"
            className="mb-4 w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none transition-colors placeholder:text-jawa-espresso/40 focus:border-jawa-turquoise"
          />
          <div className="flex flex-wrap gap-x-3 gap-y-2">
            {LETTERS.map((L) => {
              const has = Boolean(byLetter[L]?.length);
              return (
                <button
                  key={L}
                  onClick={() => has && scrollTo(L)}
                  disabled={!has}
                  data-testid={`letter-${L}`}
                  className={`font-sans text-sm uppercase transition-colors ${
                    has
                      ? "cursor-pointer font-bold text-jawa-turquoise underline decoration-jawa-turquoise/40 underline-offset-4 hover:text-jawa-espresso"
                      : "cursor-default text-jawa-nearblack/30"
                  }`}
                >
                  {L}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-12 space-y-14" data-testid="topics-list">
          {LETTERS.filter((L) => byLetter[L]?.length).map((L) => (
            <section key={L} id={`letter-${L}`} className="scroll-mt-40" data-testid={`topic-letter-${L}`}>
              <div className="flex items-baseline gap-5 border-b border-jawa-gold/40 pb-4">
                <span className="font-serif text-5xl font-medium text-jawa-gold">{L}</span>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {byLetter[L].map((t) => (
                  <div
                    key={t._id || t.title}
                    data-testid={`topic-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="border border-jawa-stone bg-white p-6 transition-colors hover:border-jawa-turquoise/60"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-serif text-xl font-medium text-jawa-espresso">{t.title}</h3>
                      {t.status === "planned" && (
                        <span className="shrink-0 border border-jawa-yellow/60 bg-jawa-yellow/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/70">
                          In preparation
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-jawa-espresso/65">{t.summary}</p>
                    {t.link ? (
                      <a
                        href={t.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`topic-link-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                      >
                        {t.cta || "Learn more"} <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    ) : t.cta ? (
                      <a
                        href={waLink(`Hello JÀWÀ — tell me more about "${t.title}".`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`topic-wa-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                      >
                        {t.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
          {topics.length === 0 && audience !== "general" && (
            <div className="border border-jawa-gold/50 bg-jawa-espresso p-10 text-center" data-testid="topics-members-only">
              <h3 className="font-serif text-2xl font-medium text-jawa-ivory">
                This collection is members-only.
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-jawa-ivory/70">
                The {AUDIENCE_TABS.find((t) => t.id === audience)?.label} A–Z lives inside membership —
                always current, written for your journey.
              </p>
              <Link
                to="/memberships"
                data-testid="topics-membership-link"
                className="mt-6 inline-flex items-center gap-2 bg-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-ivory hover:text-jawa-espresso"
              >
                Explore memberships <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}
          {topics.length === 0 && audience === "general" && (
            <p className="text-sm text-jawa-espresso/60" data-testid="topics-empty">
              Topics are being prepared — check back soon.
            </p>
          )}
        </div>

        <p className="mt-20 max-w-3xl border-t border-jawa-stone pt-8 text-xs leading-relaxed text-jawa-espresso/50" data-testid="guides-disclaimer">
          JÀWÀ A–Z Guides provide practical orientation and curated information. They are not regulated
          legal, immigration, financial or investment advice. Where a decision carries legal or financial
          weight, we will always point you to qualified professionals.
        </p>
      </main>
      <BackToTop />
      <Footer />
    </div>
  );
}
